import React, { useState, useEffect } from 'react';
import { shipmentService } from '@/services/shipmentService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faBox,
  faUser,
  faTimesCircle,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faPrint,
  faEdit,
  faSave,
  faMoneyBillWave,
  faTruckFast,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { formatTrackingCode, getAgentTrackingStatuses } from '@/utils/formatters';
import AgentReceipt from '@/components/AgentReceipt';
import { useAuth } from '@/AuthContext';

const ManageShipments = () => {
  const { isAdmin, isAgent } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [totalShipments, setTotalShipments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [fullShipment, setFullShipment] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [customStatus, setCustomStatus] = useState('');
  const [customPriceDraft, setCustomPriceDraft] = useState('');
  const [paymentReferenceDraft, setPaymentReferenceDraft] = useState('');
  const [savingCustomPrice, setSavingCustomPrice] = useState(false);
  const [savingPaymentReference, setSavingPaymentReference] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const [fullShipmentForReceipt, setFullShipmentForReceipt] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  const handleShowReceipt = async (s) => {
    setSelectedShipment(s);
    setFullShipmentForReceipt(null);
    setShowReceiptModal(true);
    setLoadingReceipt(true);

    try {
      const token = localStorage.getItem('token');
      const id = s._id || s.id;
      if (id && token) {
        const res = await shipmentService.getById(id, token);
        setFullShipmentForReceipt(res?.shipment || null);
      }
    } catch (e) {
      console.error('Failed to load full shipment for receipt', e);
    } finally {
      setLoadingReceipt(false);
    }
  };

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        limit,
        offset,
        period: periodFilter === 'all' ? '' : periodFilter,
        status: statusFilter === 'all' ? '' : statusFilter,
        q: debouncedSearch && debouncedSearch.length > 1 ? debouncedSearch : ''
      };
      let data;
      // Both admin and agent use the /all endpoint which always returns full shipment data
      if (isAdmin || isAgent) {
        try {
          data = await shipmentService.listAllPrivileged(params, token);
        } catch (_) {
          data = await shipmentService.list({ ...params, full: true }, token);
        }
      } else {
        data = await shipmentService.list({ ...params, full: true }, token);
      }
      if (!data || !Array.isArray(data.shipments)) {
        data = { shipments: [], total_shipments: 0 };
      }
      setShipments((data.shipments || []).map(s => ({
        ...s,
        // Normalize possible shapes from /shipments (full vs minimal)
        id: s.id || s._id || s.shipment_id || s.trackingCode,
        trackingCode: s.trackingCode || s.id || s._id || s.shipment_id || '',
        createdAt: s.createdAt || s.last_updated || null,
        date: (s.createdAt || s.last_updated) ? new Date(s.createdAt || s.last_updated).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : '—'
      })));
      setTotalShipments(data.total_shipments || 0);
    } catch (e) {
      console.error('Failed to load shipments', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    fetchShipments();
  }, [offset, periodFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    if (!showDetailsModal) return;
    const s = fullShipment || selectedShipment || {};
    const nextPrice =
      s.customPrice !== undefined && s.customPrice !== null
        ? String(s.customPrice)
        : '';
    const nextRef = s.paymentReferenceCode ? String(s.paymentReferenceCode) : '';
    setCustomPriceDraft(nextPrice);
    setPaymentReferenceDraft(nextRef);
  }, [showDetailsModal, fullShipment, selectedShipment]);

  const handleViewDetails = async (shipment) => {
    setSelectedShipment(shipment);
    setShowDetailsModal(true);
    setFullShipment(null);

    try {
      const token = localStorage.getItem('token');
      const id = shipment._id || shipment.id;
      if (id && token) {
        const res = await shipmentService.getById(id, token);
        setFullShipment(res?.shipment || null);
      }
    } catch (e) {
      console.error('Failed to load full shipment details', e);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (s === 'delivered') return 'bg-green-100 text-green-800';
    if (s === 'delayed') return 'bg-orange-100 text-orange-800';
    if (s === 'lost' || s === 'cancelled') return 'bg-red-100 text-red-800';
    if (s === 'shipment departed') return 'bg-indigo-100 text-indigo-800';
    if (s === 'shipment arrival sorting facility') return 'bg-yellow-100 text-yellow-800';
    if (s === 'shipment rescheduled') return 'bg-orange-100 text-orange-800';
    if (s === 'shipment available for pickup (terminal)') return 'bg-purple-100 text-purple-800';
    if (s.startsWith("int'l")) return 'bg-teal-100 text-teal-800';
    return 'bg-purple-100 text-purple-800';
  };

  const getPaymentTypeLabel = (paymentType) => {
    return paymentType === 'payNow' ? 'Paid' : 'Cash on Delivery (COD)';
  };

  const allowedStatusesFor = (s) => {
    try {
      return getAgentTrackingStatuses(s.serviceType || 'interstate') || [];
    } catch {
      return ['Origin', 'Pickup', 'In Transit', 'Out for Delivery', 'Delivered', 'Cancelled'];
    }
  };

  const handleChangeStatus = async (s, newStatus) => {
    if (!newStatus || newStatus === s.status) return;
    try {
      setUpdatingId(s.id || s._id);
      const token = localStorage.getItem('token');
      await shipmentService.updateStatus(s.id || s._id, newStatus, 'Processing', token);
      setShipments(prev => prev.map(x => ((x.id || x._id) === (s.id || s._id)) ? { ...x, status: newStatus } : x));
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCodAction = async (s, action) => {
    if (!isAgent) return;
    try {
      setUpdatingId(s.id || s._id);
      const token = localStorage.getItem('token');
      const res = await shipmentService.updateCod(s.id || s._id, action, token);
      const updated = res?.shipment;
      setShipments(prev => prev.map(x => ((x.id || x._id) === (s.id || s._id)) ? { ...x, ...updated } : x));
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to update COD status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCancelShipment = async (s) => {
    if (!isAdmin) return;
    if (!window.confirm(`Are you sure you want to cancel shipment ${s.trackingCode}?`)) return;

    try {
      setUpdatingId(s.id || s._id);
      const token = localStorage.getItem('token');
      await shipmentService.cancelShipment(s.id || s._id, 'Cancelled', token);
      setShipments(prev => prev.map(x => ((x.id || s._id) === (s.id || s._id)) ? { ...x, status: 'Cancelled' } : x));
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to cancel shipment');
    } finally {
      setUpdatingId(null);
    }
  };

  const isPaymentConfirmed = (paymentStatus) => {
    const s = String(paymentStatus || '').toLowerCase();
    return s === 'paid' || s === 'confirmed' || s === 'payment confirmed';
  };

  const handleSaveCustomPrice = async (shipmentId) => {
    if (!isAgent) return;
    const value = Number(String(customPriceDraft || '').trim());
    if (!value || !Number.isFinite(value) || value <= 0) {
      alert('Enter a valid custom price');
      return;
    }
    try {
      setSavingCustomPrice(true);
      const token = localStorage.getItem('token');
      const res = await shipmentService.setCustomPrice(shipmentId, value, token);
      const updated = res?.shipment;
      if (updated) {
        setFullShipment(updated);
        setSelectedShipment(prev => (prev ? { ...prev, ...updated } : prev));
        setShipments(prev => prev.map(x => ((x._id || x.id) === shipmentId) ? { ...x, ...updated } : x));
      }
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to save custom price');
    } finally {
      setSavingCustomPrice(false);
    }
  };

  const handleSavePaymentReference = async (shipmentId) => {
    if (!isAgent) return;
    const code = String(paymentReferenceDraft || '').trim();
    if (!code) {
      alert('Enter a payment reference code');
      return;
    }
    try {
      setSavingPaymentReference(true);
      const token = localStorage.getItem('token');
      const res = await shipmentService.savePaymentReference(shipmentId, code, token);
      const updated = res?.shipment;
      if (updated) {
        setFullShipment(updated);
        setSelectedShipment(prev => (prev ? { ...prev, ...updated } : prev));
        setShipments(prev => prev.map(x => ((x._id || x.id) === shipmentId) ? { ...x, ...updated } : x));
      }
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to save payment reference');
    } finally {
      setSavingPaymentReference(false);
    }
  };

  const handleConfirmPayment = async (shipmentId, method) => {
    if (!(isAgent || isAdmin)) return;
    try {
      setConfirmingPayment(true);
      const token = localStorage.getItem('token');
      const res = await shipmentService.confirmPayment(shipmentId, method, token);
      const updated = res?.shipment;
      if (updated) {
        setFullShipment(updated);
        setSelectedShipment(prev => (prev ? { ...prev, ...updated } : prev));
        setShipments(prev => prev.map(x => ((x._id || x.id) === shipmentId) ? { ...x, ...updated } : x));
      }
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to confirm payment');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleMarkAsPaid = async (s) => {
    if (!isAgent) return;
    const id = s.id || s._id;
    try {
      setUpdatingId(id);
      const token = localStorage.getItem('token');
      const res = await shipmentService.confirmPayment(id, 'cash', token);
      const updated = res?.shipment;
      if (updated) {
        setShipments(prev => prev.map(x => ((x._id || x.id) === id) ? { ...x, ...updated } : x));
      }
    } catch (e) {
      alert(e?.response?.data?.msg || e?.message || 'Failed to mark as paid');
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredShipments = shipments.filter(s => {
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      ((s.trackingCode || '').toLowerCase().includes(term)) ||
      ((s.receiverName || '').toLowerCase().includes(term)) ||
      ((s.senderName || '').toLowerCase().includes(term));
    return matchesStatus && matchesSearch;
  });

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'today': return "Today's Shipments";
      case 'week': return "Past 7 Days Shipments";
      case 'month': return "Past 30 Days Shipments";
      default: return isAdmin ? "All User Shipments" : "All Shipments";
    }
  };

  const firstFilled = (...values) => {
    for (const value of values) {
      if (value === 0) return value;
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (value) return value;
    }
    return '—';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-800">{getPeriodLabel()}</h2>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
              <FontAwesomeIcon icon={faSearch} size="sm" />
            </span>
            <input
              type="text"
              placeholder="Search tracking, sender, receiver..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm w-64"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setOffset(0); }}
            />
          </div>
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm max-w-[160px]"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
          >
            <option value="all">All Statuses</option>
            <option value="Shipment registered">Registered (Agent)</option>
            <option value="Shipment booked">Booked (User)</option>
            <option value="Shipment transfer to third-party">Int'l Transfer</option>
            <option value="Shipment on hold">On Hold</option>
            <option value="Rider picked">Rider Picked</option>
            <option value="Out for delivery">Out for Delivery</option>
            <option value="Shipment in transit">In Transit</option>
            <option value="Shipment departed">Departed (Lagos)</option>
            <option value="Shipment arrival sorting facility">Sorting Facility</option>
            <option value="Shipment arrived service center">Arrived Hub</option>
            <option value="Return to sender">Return to Sender</option>
            <option value="Shipment Rescheduled">Rescheduled</option>
            <option value="Shipment available for pickup">Available for Pickup</option>
            <option value="Int'l Shipment undergoing custom clearance">Custom Clearance</option>
            <option value="Int'l Shipment arrival sorting warehouse">Sorting (USA)</option>
            <option value="Int'l Shipment arrival sorting warehouse">Sorting (UK)</option>
            <option value="Delivered">Delivered</option>
            <option value="Delayed">Delayed</option>
            <option value="Lost">Lost</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-semibold text-gray-700 bg-white shadow-sm"
            value={periodFilter}
            onChange={(e) => {
              setPeriodFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="all">📅 All Time</option>
            <option value="today">🕒 Today</option>
            <option value="week">📅 Last 7 Days</option>
            <option value="month">📅 Last 30 Days</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-4">Tracking Code</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Sender / Receiver</th>
                <th className="px-6 py-4">Price</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Payment</th>
                <th className="px-6 py-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                      Loading shipments...
                    </div>
                  </td>
                </tr>
              ) : filteredShipments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                    No shipments found.
                  </td>
                </tr>
              ) : (
                filteredShipments.map((s) => (
                  <tr key={s.id || s._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {formatTrackingCode(s.trackingCode || s.id)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {s.date || (s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800">{s.senderName || '—'}</span>
                        <span className="text-xs text-gray-500">to {s.receiverName || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">
                      ₦{Number(s.price || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {editingStatusId === (s.id || s._id) ? (
                          <div className="flex items-center gap-1 animate-in fade-in zoom-in-95">
                            <input
                              type="text"
                              value={customStatus}
                              onChange={(e) => setCustomStatus(e.target.value)}
                              className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500 w-24 sm:w-32"
                              placeholder="New Status..."
                              autoFocus
                            />
                            <button
                              onClick={() => {
                                if (customStatus.trim()) {
                                  handleChangeStatus(s, customStatus.trim());
                                }
                                setEditingStatusId(null);
                              }}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Save Status"
                            >
                              <FontAwesomeIcon icon={faSave} />
                            </button>
                            <button
                              onClick={() => setEditingStatusId(null)}
                              className="text-gray-400 hover:text-gray-600 p-1 text-lg leading-none"
                              title="Cancel"
                            >
                              &times;
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 group">
                            <div className="relative">
                              <select
                                className={`appearance-none pr-8 pl-3 py-1.5 text-[10px] sm:text-xs font-bold rounded-full border shadow-sm transition-all focus:ring-2 focus:ring-offset-1 max-w-[140px] truncate ${getStatusColor(s.status || 'Pending')} ${updatingId === (s.id || s._id) || s.status === 'Cancelled' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'} `}
                                style={{
                                  borderColor: 'currentColor',
                                  borderWidth: '1.5px'
                                }}
                                value={s.status || 'Pending'}
                                onChange={(e) => handleChangeStatus(s, e.target.value)}
                                disabled={!!updatingId || s.status === 'Cancelled'}
                                title={s.status === 'Cancelled' ? "Cancelled shipments cannot be updated" : "Click to update status"}
                              >
                                {allowedStatusesFor(s).map(st => (
                                  <option value={st} key={st} className="bg-white text-gray-900 font-medium">
                                    {st}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] opacity-60 group-hover:opacity-100 transition-opacity">
                                <FontAwesomeIcon icon={faChevronDown} />
                              </div>
                            </div>

                            {isAdmin && (
                              <button
                                onClick={() => {
                                  setEditingStatusId(s.id || s._id);
                                  setCustomStatus(s.status || '');
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-600"
                                title="Edit status manually"
                              >
                                <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span>{getPaymentTypeLabel(s.paymentType || 'payNow')} <span className="text-xs text-gray-500">({s.paymentStatus || 'Pending'})</span></span>
                          {isPaymentConfirmed(s.paymentStatus) && (
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 animate-in fade-in zoom-in duration-300" title="Payment Confirmed" />
                          )}
                        </div>
                        {isAgent && !isPaymentConfirmed(s.paymentStatus) && (
                          <button
                            onClick={() => handleMarkAsPaid(s)}
                            disabled={!!updatingId}
                            className="px-2 py-1 text-xs rounded bg-green-600 text-white border border-green-700 hover:bg-green-700 disabled:opacity-50 shadow-sm"
                            title="Mark payment as confirmed/paid"
                          >
                            Paid
                          </button>
                        )}
                        {s.paymentType === 'cashOnDelivery' && (
                          <div className="text-xs">
                            <span className="font-medium">COD:</span> ₦{Number(s.codAmount || 0).toLocaleString()} • <span className={`inline-block px-2 py-0.5 rounded-full border ${s.codStatus === 'remitted' ? 'text-green-700 border-green-300 bg-green-50' : s.codStatus === 'collected' ? 'text-blue-700 border-blue-300 bg-blue-50' : 'text-yellow-700 border-yellow-300 bg-yellow-50'}`}>
                              {s.codStatus === 'remitted' ? 'Remitted' : s.codStatus === 'collected' ? 'Collected' : 'To Collect'}
                            </span>
                            {isAgent && (
                              <div className="mt-1 flex gap-2">
                                {s.codStatus === 'to_collect' && (
                                  <button
                                    onClick={() => handleCodAction(s, 'collect')}
                                    disabled={!!updatingId}
                                    className="px-2 py-1 text-xs rounded bg-blue-600 text-white border border-blue-700 hover:bg-blue-700 disabled:opacity-50 shadow-sm"
                                    title="Mark COD as collected"
                                  >
                                    Mark Collected
                                  </button>
                                )}
                                {s.codStatus === 'collected' && (
                                  <button
                                    onClick={() => handleCodAction(s, 'remit')}
                                    disabled={!!updatingId}
                                    className="px-2 py-1 text-xs rounded bg-green-600 text-white border border-green-700 hover:bg-green-700 disabled:opacity-50 shadow-sm"
                                    title="Mark COD as remitted"
                                  >
                                    Mark Remitted
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleViewDetails(s)}
                          className="px-3 py-1.5 border border-red-200 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg font-medium flex items-center justify-center transition-all"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1.5" />
                          Details
                        </button>
                        {isAgent && (
                          <button
                            onClick={() => handleShowReceipt(s)}
                            className="px-3 py-1.5 border border-blue-200 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg font-medium flex items-center justify-center text-xs transition-all"
                          >
                            <FontAwesomeIcon icon={faPrint} className="mr-1.5" />
                            Receipt
                          </button>
                        )}
                        {isAdmin && s.status !== 'Cancelled' && (
                          <button
                            onClick={() => handleCancelShipment(s)}
                            disabled={!!updatingId}
                            className="px-3 py-1.5 border border-red-200 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg font-medium flex items-center justify-center text-xs transition-all disabled:opacity-50"
                          >
                            <FontAwesomeIcon icon={faTimesCircle} className="mr-1.5" />
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalShipments > limit && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing {offset + 1} to {Math.min(offset + limit, totalShipments)} of {totalShipments}
            </span>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(prev => Math.max(0, prev - limit))}
                className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-white"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                disabled={offset + limit >= totalShipments}
                onClick={() => setOffset(prev => prev + limit)}
                className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-white"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedShipment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDetailsModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Shipment Status</h2>
              <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase font-bold mb-1">Tracking Code</p>
                  <p className="font-mono font-bold text-red-600">{formatTrackingCode(selectedShipment.trackingCode)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <p className="text-xs text-gray-500 uppercase font-bold mb-1">Current Status</p>
                  <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border ${getStatusColor(selectedShipment.status)}`} style={{ borderColor: 'currentColor', borderWidth: '1.5px' }}>
                    {selectedShipment.status}
                  </span>
                </div>
              </div>

              {/* Status History Timeline */}
              {fullShipment?.statusHistory && fullShipment.statusHistory.length > 0 && (
                <div className="mt-8 pt-8 border-t border-gray-100">
                  <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                    <FontAwesomeIcon icon={faTruckFast} className="text-red-500 mr-2" />
                    Status History
                  </h4>
                  <div className="space-y-4">
                    {fullShipment.statusHistory.map((history, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${idx === 0 ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                          {idx !== fullShipment.statusHistory.length - 1 && (
                            <div className="w-0.5 h-full bg-gray-100 my-1"></div>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-800">{history.status}</p>
                          <p className="text-[10px] text-gray-500">
                            {new Date(history.updatedAt || history.timestamp || Date.now()).toLocaleString()} • {history.updatedBy || 'System'}
                          </p>
                          {history.note && (
                            <p className="text-xs text-gray-600 mt-1 italic">"{history.note}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(() => {
                const detailShipment = fullShipment || selectedShipment || {};
                const senderName = firstFilled(detailShipment.senderName, selectedShipment.senderName);
                const senderPhone = firstFilled(detailShipment.senderNumber, detailShipment.senderPhone, selectedShipment.senderNumber, selectedShipment.senderPhone);
                const senderEmail = firstFilled(detailShipment.senderEmail, selectedShipment.senderEmail);
                const senderAddress = firstFilled(detailShipment.senderAddress, detailShipment.pickupLocation, selectedShipment.senderAddress, selectedShipment.pickupLocation);
                const senderLocation = firstFilled(detailShipment.senderState, detailShipment.pickupLocation);
                const receiverName = firstFilled(detailShipment.receiverName, selectedShipment.receiverName);
                const receiverPhone = firstFilled(detailShipment.receiverNumber, selectedShipment.receiverNumber);
                const receiverEmail = firstFilled(detailShipment.receiverEmail, selectedShipment.receiverEmail);
                const receiverAddress = firstFilled(detailShipment.receiverAddress, detailShipment.dropoffLocation, selectedShipment.receiverAddress, selectedShipment.dropoffLocation);
                const receiverLocation = firstFilled(detailShipment.receiverState, detailShipment.receiverCity, detailShipment.country);

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-500" /> Sender Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Name:</span> {senderName}</p>
                        <p><span className="text-gray-500">Phone:</span> {senderPhone}</p>
                        <p><span className="text-gray-500">Email:</span> {senderEmail}</p>
                        {senderLocation && <p><span className="text-gray-500">Location:</span> {senderLocation}</p>}
                        <p><span className="text-gray-500">Address:</span> {senderAddress}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faUser} className="mr-2 text-green-500" /> Receiver Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-500">Name:</span> {receiverName}</p>
                        <p><span className="text-gray-500">Phone:</span> {receiverPhone}</p>
                        <p><span className="text-gray-500">Email:</span> {receiverEmail}</p>
                        {receiverLocation && <p><span className="text-gray-500">Location:</span> {receiverLocation}</p>}
                        <p><span className="text-gray-500">Address:</span> {receiverAddress}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="border-t border-gray-100 pt-6">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faBox} className="mr-2 text-purple-500" /> Package Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 font-bold text-[10px] uppercase">Weight</p>
                    <p className="font-medium">{(selectedShipment.weight || fullShipment?.weight) ? `${selectedShipment.weight || fullShipment?.weight}kg` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-bold text-[10px] uppercase">Items</p>
                    <p className="font-medium">{selectedShipment.numberOfItems || fullShipment?.numberOfItems || '1'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-bold text-[10px] uppercase">Service</p>
                    <p className="font-medium capitalize">
                      {(() => {
                        const s = selectedShipment.serviceType || fullShipment?.serviceType || 'interstate';
                        const sub = selectedShipment.internationalSubtype || fullShipment?.internationalSubtype;
                        const del = selectedShipment.deliveryType || fullShipment?.deliveryType;

                        if (s === 'international' && sub) {
                          return `International (${sub})`;
                        }
                        if (s === 'interstate' && del) {
                          return `Interstate (${del})`;
                        }
                        return s;
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-bold text-[10px] uppercase">Price</p>
                    {(() => {
                      const s = fullShipment || selectedShipment || {};
                      const hasNegotiated = s.customPrice !== undefined && s.customPrice !== null;
                      const display = typeof s.price === 'number' ? s.price : (Number(s.price) || 0);
                      const original = (s.originalPrice !== undefined && s.originalPrice !== null)
                        ? (typeof s.originalPrice === 'number' ? s.originalPrice : (Number(s.originalPrice) || null))
                        : null;
                      return (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-green-600 font-bold">₦{display.toLocaleString()}</p>
                            {hasNegotiated && (
                              <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                Negotiated
                              </span>
                            )}
                          </div>
                          {hasNegotiated && original !== null && (
                            <p className="text-[11px] text-gray-500">
                              Original: ₦{Number(original).toLocaleString()}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {(fullShipment?.contentList || selectedShipment.contentList) && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <p className="text-xs text-gray-500 uppercase font-bold mb-1">Contents</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{fullShipment?.contentList || selectedShipment.contentList}</p>
                  </div>
                )}
              </div>

              {(() => {
                const s = fullShipment || selectedShipment || {};
                const shipmentId = s._id || s.id || selectedShipment._id || selectedShipment.id;
                const paymentStatus = s.paymentStatus || 'Pending';
                const paymentMethod = s.paymentMethod || '';
                const paymentReferenceCode = s.paymentReferenceCode || '';
                const paymentReferenceSavedAt = s.paymentReferenceSavedAt || null;
                const paymentConfirmedAt = s.paymentConfirmedAt || null;
                const receipt = s.paymentReceiptData || '';
                const confirmed = isPaymentConfirmed(paymentStatus);
                const refTs = paymentReferenceSavedAt ? new Date(paymentReferenceSavedAt) : null;
                const confirmedTs = paymentConfirmedAt ? new Date(paymentConfirmedAt) : null;
                const showConfirmBank = (isAgent || isAdmin) && !!receipt && !confirmed;
                const showConfirmCash = (isAgent || isAdmin) && String(paymentMethod) === 'cash' && String(paymentStatus) === 'Awaiting Cash Payment';

                return (
                  <div className="border-t border-gray-100 pt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-gray-800 flex items-center">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" /> Pricing & Payment
                      </h4>
                      <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full border ${confirmed ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                        {confirmed ? 'Payment Confirmed' : 'Pending Payment'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Payment Status</p>
                        <p className="font-semibold text-gray-900">{paymentStatus || '—'}</p>
                        {confirmedTs && (
                          <p className="text-[11px] text-gray-600 mt-1">
                            Confirmed on {confirmedTs.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Payment Method</p>
                        <p className="font-semibold text-gray-900">{paymentMethod || '—'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Payment Reference Code</p>
                        <p className="font-mono font-semibold text-gray-900 break-all">{paymentReferenceCode || '—'}</p>
                        {refTs && (
                          <p className="text-[11px] text-gray-600 mt-1">
                            Confirmed on {refTs.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">Receipt</p>
                        {receipt ? (
                          <a href={receipt} target="_blank" rel="noreferrer" className="text-xs text-blue-600 font-bold underline">
                            View uploaded receipt
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600">—</p>
                        )}
                      </div>
                    </div>

                    {receipt && (
                      <div className="bg-white p-3 rounded-lg border border-gray-100">
                        <img src={receipt} alt="Payment receipt" className="w-full max-h-80 object-contain rounded-lg border border-gray-100" />
                      </div>
                    )}

                    {(isAgent || isAdmin) && (
                      <div className="flex flex-col sm:flex-row gap-3">
                        {showConfirmBank && (
                          <button
                            disabled={confirmingPayment}
                            onClick={() => handleConfirmPayment(shipmentId, 'bank_transfer')}
                            className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            Confirm Payment
                          </button>
                        )}
                        {showConfirmCash && (
                          <button
                            disabled={confirmingPayment}
                            onClick={() => handleConfirmPayment(shipmentId, 'cash')}
                            className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            Confirm Cash Payment
                          </button>
                        )}
                      </div>
                    )}

                    {isAgent && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Set Custom Price</p>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={customPriceDraft}
                              onChange={(e) => setCustomPriceDraft(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="Enter amount"
                            />
                            <button
                              disabled={savingCustomPrice || !shipmentId}
                              onClick={() => handleSaveCustomPrice(shipmentId)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                          <p className="text-[10px] uppercase font-bold text-gray-500 mb-2">Payment Reference Code</p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={paymentReferenceDraft}
                              onChange={(e) => setPaymentReferenceDraft(e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                              placeholder="Enter reference"
                            />
                            <button
                              disabled={savingPaymentReference || !shipmentId}
                              onClick={() => handleSavePaymentReference(shipmentId)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-2">Saving a reference automatically confirms payment.</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && selectedShipment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReceiptModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex justify-between items-center mb-4 print:hidden">
              <h3 className="text-lg font-bold">Shipment Receipt</h3>
              <button onClick={() => setShowReceiptModal(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
              </button>
            </div>

            <div id="printable-receipt">
              {loadingReceipt ? (
                <div className="flex flex-col items-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-2"></div>
                  <p className="text-xs text-gray-500">Preparing receipt...</p>
                </div>
              ) : (
                <AgentReceipt shipment={fullShipmentForReceipt || selectedShipment} />
              )}
            </div>

            <div className="mt-6 flex gap-3 print:hidden">
              <button
                onClick={() => window.print()}
                disabled={loadingReceipt}
                className="flex-1 bg-red-600 text-white py-2 rounded-lg font-bold hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faPrint} className="mr-2" />
                Print Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageShipments;
